import { ChangeDetectorRef, Component, HostBinding, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription, switchMap } from 'rxjs';

import { ReportQuery, ReportQueryService, ReportResult } from '../../../services/report-query';
import { dashboardErrorMessage, dashboardLayoutStyle } from '../../widget-and-graphs.models';
import { DashboardListColumn } from '../dashboard-list-widget/dashboard-list-widget';
import { DashboardWidgetLayout } from '../dashboard-grid/dashboard-grid';

@Component({
  selector: 'app-dashboard-position-widget',
  standalone: false,
  templateUrl: './dashboard-position-widget.html',
  styleUrl: './dashboard-position-widget.scss',
})
export class DashboardPositionWidget implements OnChanges, OnDestroy {
  @Input() iconSvg = '';
  @Input({ required: true }) title = '';
  @Input({ required: true }) query!: ReportQuery;
  @Input({ required: true }) columns: DashboardListColumn[] = [];
  @Input() subtitle = '';
  @Input() emptyText = 'No posted values in the authorised scope.';
  @Input() currencyCode = 'USD';
  @Input() locale = 'en-US';
  @Input() layout: DashboardWidgetLayout = { xs: { width: 12, height: 4 } };

  loading = false;
  error = '';
  result?: ReportResult;
  private request?: Subscription;

  constructor(private readonly reports: ReportQueryService, private readonly cdr: ChangeDetectorRef) {}

  @HostBinding('attr.style') get layoutStyles(): string { return dashboardLayoutStyle(this.layout); }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['query'] || changes['columns']) this.load();
  }

  ngOnDestroy(): void { this.request?.unsubscribe(); }

  get rows(): Array<Record<string, unknown>> { return this.result?.rows ?? []; }

  formatValue(value: unknown, column: DashboardListColumn): string {
    if (value === null || value === undefined || value === '') return '—';
    if (column.format === 'currency') {
      return new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currencyCode, maximumFractionDigits: 2 }).format(Number(value));
    }
    if (column.format === 'number') return new Intl.NumberFormat(this.locale, { maximumFractionDigits: 2 }).format(Number(value));
    return String(value);
  }

  private load(): void {
    this.request?.unsubscribe();
    this.result = undefined;
    this.error = '';
    if (!this.query?.from || !this.query?.duration || !this.columns.length) {
      this.error = 'This widget requires a bounded RQL query and display columns.';
      return;
    }
    const query: ReportQuery = { ...this.query, limit: Math.min(this.query.limit ?? 20, 20), offset: 0, include_total: false };
    this.loading = true;
    this.request = this.reports.verify(query).pipe(switchMap(() => this.reports.execute(query))).subscribe({
      next: result => { this.result = result; this.loading = false; this.cdr.detectChanges(); },
      error: error => { this.error = dashboardErrorMessage(error); this.loading = false; this.cdr.detectChanges(); },
    });
  }
}
